/**
 * AI Model Configuration Component (READ-ONLY)
 * 
 * Displays the AI model configurations for the expert.
 * Configurations are managed by iMaven admins in the CRM system.
 * Experts can view but not modify these settings.
 */

import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

// ============================================
// ANIMATIONS
// ============================================

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.3px;
`;

const SectionSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  margin-bottom: 24px;
  margin-top: 0;
`;

const ManagedByBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  font-size: 12px;
  color: #a5b4fc;
  margin-bottom: 20px;
`;

const CategoryBadge = styled.span`
  background: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(16, 185, 129, 0.4);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  color: #34d399;
  font-weight: 600;
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const TaskCard = styled.div`
  background: rgba(25, 30, 45, 0.6);
  border: 1px solid rgba(100, 150, 200, 0.15);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }
`;

const TaskHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const TaskIcon = styled.span`
  font-size: 20px;
`;

const TaskName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const ConfigRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const ConfigLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const ConfigValue = styled.span`
  font-size: 12px;
  color: #a5b4fc;
  font-weight: 500;
`;

const ModelBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(99, 102, 241, 0.15);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  font-size: 11px;
  color: #a5b4fc;
  font-weight: 600;
`;

const CostBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => {
    if (props.$multiplier <= 0.2) return 'rgba(16, 185, 129, 0.2)';
    if (props.$multiplier <= 0.5) return 'rgba(16, 185, 129, 0.15)';
    if (props.$multiplier <= 1) return 'rgba(99, 102, 241, 0.15)';
    if (props.$multiplier <= 2) return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(239, 68, 68, 0.15)';
  }};
  color: ${props => {
    if (props.$multiplier <= 0.2) return '#34d399';
    if (props.$multiplier <= 0.5) return '#6ee7b7';
    if (props.$multiplier <= 1) return '#a5b4fc';
    if (props.$multiplier <= 2) return '#fbbf24';
    return '#f87171';
  }};
`;

const CreativityBar = styled.div`
  width: 60px;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
`;

const CreativityFill = styled.div`
  height: 100%;
  background: linear-gradient(to right, #3b82f6, #6366f1, #a855f7);
  border-radius: 3px;
  width: ${props => props.$value}%;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 10px;
  padding: 16px;
  margin-top: 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoIcon = styled.span`
  font-size: 20px;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #60a5fa;
  margin-bottom: 4px;
`;

const InfoText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
`;

// ============================================
// TASK ICONS
// ============================================

const TASK_ICONS = {
  default: '⚙️',
  widget: '💬',
  observer: '👁️',
  summarizer: '📝',
  caseAnalysis: '📊',
  directiveGenerator: '✨',
  directiveUpdater: '🔄',
  synthoraAssistant: '🤖',
  applicationReviewer: '📋',
  workspaceAssistant: '🛠️',
  profileAssistant: '👤',
  dashboardAssistant: '📈',
  brainLearning: '🧠',
  copilot: '🚀',
  conversationReviewer: '🔍'
};

// ============================================
// MAIN COMPONENT
// ============================================

const AIModelConfig = () => {
  const axios = useAuthenticatedAxios();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('default');
  const [configurations, setConfigurations] = useState({});
  const [taskTypes, setTaskTypes] = useState({});
  const [availableModels, setAvailableModels] = useState([]);

  useEffect(() => {
    loadConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfigurations = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get('/category-model-config/my-config');
      setCategory(response.data.category || 'Not Assigned');
      setSource(response.data.source || 'default');
      setConfigurations(response.data.configurations || {});
      setTaskTypes(response.data.taskTypes || {});
      setAvailableModels(response.data.availableModels || []);
    } catch (err) {
      console.error('Error loading model configurations:', err);
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const getModelInfo = (modelName) => {
    return availableModels.find(m => m.id === modelName);
  };

  const getResponseLengthLabel = (length) => {
    switch (length) {
      case 'short': return '~2K tokens';
      case 'medium': return '~8K tokens';
      case 'long': return '~16K tokens';
      case 'custom': return 'Custom';
      default: return length;
    }
  };

  if (loading) {
    return (
      <Container>
        <SectionTitle>🤖 AI Model Configuration</SectionTitle>
        <LoadingSpinner>Loading configurations...</LoadingSpinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <SectionTitle>🤖 AI Model Configuration</SectionTitle>
        <SectionSubtitle style={{ color: '#f87171' }}>{error}</SectionSubtitle>
      </Container>
    );
  }

  // Filter to show only the most relevant tasks
  const primaryTasks = ['widget', 'caseAnalysis', 'directiveGenerator', 'observer'];
  const secondaryTasks = Object.keys(configurations).filter(
    t => !primaryTasks.includes(t) && t !== 'default'
  );

  return (
    <Container>
      <SectionTitle>
        <span>🤖</span>
        AI Model Configuration
      </SectionTitle>
      <SectionSubtitle>
        Your AI model settings are configured based on your category.
      </SectionSubtitle>

      <ManagedByBadge>
        <span>🔒</span>
        Managed by iMaven
        {category !== 'Not Assigned' && (
          <>
            <span style={{ margin: '0 8px', opacity: 0.5 }}>|</span>
            <CategoryBadge>{category}</CategoryBadge>
          </>
        )}
      </ManagedByBadge>

      <ConfigGrid>
        {/* Default Config */}
        {configurations.default && (
          <TaskCard>
            <TaskHeader>
              <TaskIcon>{TASK_ICONS.default}</TaskIcon>
              <TaskName>Default Settings</TaskName>
            </TaskHeader>
            {renderConfigDetails(configurations.default)}
          </TaskCard>
        )}

        {/* Primary Tasks */}
        {primaryTasks.map(taskType => {
          const config = configurations[taskType];
          if (!config) return null;
          
          return (
            <TaskCard key={taskType}>
              <TaskHeader>
                <TaskIcon>{TASK_ICONS[taskType]}</TaskIcon>
                <TaskName>{taskTypes[taskType] || taskType}</TaskName>
              </TaskHeader>
              {renderConfigDetails(config)}
            </TaskCard>
          );
        })}

        {/* Secondary Tasks */}
        {secondaryTasks.map(taskType => {
          const config = configurations[taskType];
          if (!config) return null;
          
          return (
            <TaskCard key={taskType}>
              <TaskHeader>
                <TaskIcon>{TASK_ICONS[taskType] || '⚙️'}</TaskIcon>
                <TaskName>{taskTypes[taskType] || taskType}</TaskName>
              </TaskHeader>
              {renderConfigDetails(config)}
            </TaskCard>
          );
        })}
      </ConfigGrid>

      <InfoBox>
        <InfoIcon>ℹ️</InfoIcon>
        <InfoContent>
          <InfoTitle>About AI Model Settings</InfoTitle>
          <InfoText>
            These settings determine which AI models are used for different tasks in your iMaven workspace.
            Settings are configured by iMaven based on your category ({category}) to ensure optimal performance 
            and cost efficiency. If you need different settings, please contact iMaven support.
          </InfoText>
        </InfoContent>
      </InfoBox>
    </Container>
  );

  function renderConfigDetails(config) {
    const modelInfo = getModelInfo(config.model_name);
    
    return (
      <>
        <ConfigRow>
          <ConfigLabel>Model</ConfigLabel>
          <ModelBadge>
            {modelInfo?.displayName || config.model_name}
            {modelInfo && (
              <CostBadge $multiplier={modelInfo.costMultiplier}>
                {modelInfo.priceInfo}
              </CostBadge>
            )}
          </ModelBadge>
        </ConfigRow>
        <ConfigRow>
          <ConfigLabel>Creativity</ConfigLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CreativityBar>
              <CreativityFill $value={config.creativity} />
            </CreativityBar>
            <ConfigValue>{config.creativity}%</ConfigValue>
          </div>
        </ConfigRow>
        <ConfigRow>
          <ConfigLabel>Response Length</ConfigLabel>
          <ConfigValue>{getResponseLengthLabel(config.responseLength)}</ConfigValue>
        </ConfigRow>
      </>
    );
  }
};

export default AIModelConfig;
